import Table from "../../components/RecordTable";
import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  Modal,
  Form,
  InputNumber,
  message,
  Tag,
  Popconfirm,
  Typography,
  Divider,
  Row,
  Col,
  Spin,
  Input,
  DatePicker,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  GiftOutlined,
  SettingOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import styled, { ThemeProvider } from "styled-components";
import theme from "../../config/theme";
import { pointsSettingAPI, rewardsAPI } from "../../services/api";
import { PageHero } from "../../components/Illustration";

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: ${(props) => props.theme.spacing.xl}px;
  background: ${(props) => props.theme.colors.background};
  min-height: 100vh;
`;

const StyledCard = styled(Card)`
  border-radius: ${(props) => props.theme.borderRadius.lg}px;
  box-shadow: ${(props) => props.theme.shadows.md};
  margin-bottom: ${(props) => props.theme.spacing.xl}px;
  border: 1px solid ${(props) => props.theme.colors.borderLight};

  .ant-card-head {
    background: linear-gradient(
      135deg,
      ${(props) => props.theme.gradients.primary.join(", ")}
    );
    border-radius: ${(props) => props.theme.borderRadius.lg}px
      ${(props) => props.theme.borderRadius.lg}px 0 0;
    border: none;

    .ant-card-head-title {
      color: ${(props) => props.theme.colors.textInverse};
      font-weight: 600;
      font-size: 18px;
      display: flex;
      align-items: center;
      gap: ${(props) => props.theme.spacing.sm}px;
    }
  }
`;

const PointsInputCard = styled(Card)`
  border-radius: ${(props) => props.theme.borderRadius.md}px;
  border: 2px solid ${(props) => props.theme.colors.borderLight};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: ${(props) => props.theme.shadows.sm};
  }
`;

const InputLabel = styled.div`
  font-weight: 600;
  color: ${(props) => props.theme.colors.textPrimary};
  margin-bottom: ${(props) => props.theme.spacing.sm}px;
  font-size: 14px;
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;

  .ant-input-number-input {
    height: 48px;
    font-size: 16px;
  }
`;

const GradientButton = styled(Button)`
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.gradients.primary.join(", ")}
  );
  border: none;
  color: white;
  font-weight: 600;
  height: 48px;

  &:hover {
    background: linear-gradient(
      135deg,
      ${(props) => props.theme.gradients.primary[1]},
      ${(props) => props.theme.gradients.primary[0]}
    );
    color: white;
  }
`;

const StatusTag = styled(Tag)`
  border-radius: ${(props) => props.theme.borderRadius.sm}px;
  padding: 4px 12px;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const PointsSettingsPage = ({ claimsOnly = false }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [settingsId, setSettingsId] = useState(null);

  const [pointsConfig, setPointsConfig] = useState({
    doctorAppointment: 50,
    labTest: 30,
    referralPointsForReferrer: 100,
    pointsToCurrencyRate: 1,
    referralPointsForNewUser: 50,
  });

  const [currentPoints, setCurrentPoints] = useState(0);
  const [targetPoints, setTargetPoints] = useState(200000);
  const [savingProgress, setSavingProgress] = useState(false);
  const [milestones, setMilestones] = useState([]);

  const [userRequests, setUserRequests] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claimPage, setClaimPage] = useState(1);
  const [claimTotal, setClaimTotal] = useState(0);
  const fetchClaims = async () => {
    setClaimsLoading(true);
    try {
      const response = await rewardsAPI.getClaims({ page: claimPage, limit: 10 });
      setClaimTotal(response.data.pagination.total);
      setUserRequests(response.data.claims.map(claim => ({
        id: claim._id, userName: claim.user?.id?.name || claim.user?.name,
        email: claim.user?.id?.email || claim.user?.email, currentPoints: claim.totalPoints,
        requestedMilestone: claim.milestone?.name, requestedPoints: claim.milestone?.points,
        status: claim.claimRequest, requestDate: dayjs(claim.createdAt).format('DD MMM YYYY'),
      })));
    } catch { message.error('Unable to load reward claims'); }
    finally { setClaimsLoading(false); }
  };
  useEffect(() => { if (claimsOnly) fetchClaims(); }, [claimPage, claimsOnly]);

  const [profileVerificationFee, setProfileVerificationFee] = useState(0);
  const [savingVerificationFee, setSavingVerificationFee] = useState(false);

  const [labSlotConfig, setLabSlotConfig] = useState({ labSlotStart: 8, labSlotEnd: 18, holidays: [], labCancellationHours: 8, labCancellationPolicy: "" });
  const [savingLabSlot, setSavingLabSlot] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [form] = Form.useForm();

  // Fetch settings on component mount
  useEffect(() => {
    if (!claimsOnly) {
      fetchSettings();
      fetchLabSlotSettings();
      fetchVerificationFee();
    }
  }, [claimsOnly]);

  const fetchVerificationFee = async () => {
    try {
      const res = await pointsSettingAPI.getProfileVerificationFee();
      if (res?.success) {
        setProfileVerificationFee(res.data.profileVerificationFee ?? 0);
      }
    } catch {}
  };

  const handleSaveVerificationFee = async () => {
    try {
      setSavingVerificationFee(true);
      await pointsSettingAPI.updateProfileVerificationFee(profileVerificationFee);
      message.success("Profile verification fee saved");
    } catch {
      message.error("Failed to save verification fee");
    } finally {
      setSavingVerificationFee(false);
    }
  };

  const fetchLabSlotSettings = async () => {
    try {
      const res = await pointsSettingAPI.getLabSlotSettings();
      if (res?.success) {
        setLabSlotConfig({
          labSlotStart: res.data.labSlotStart ?? 8,
          labSlotEnd: res.data.labSlotEnd ?? 18,
          holidays: res.data.holidays || [],
          labCancellationHours: res.data.labCancellationHours ?? 8,
          labCancellationPolicy: res.data.labCancellationPolicy || "",
        });
      }
    } catch {}
  };

  const handleSaveLabSlotSettings = async () => {
    if (labSlotConfig.labSlotStart >= labSlotConfig.labSlotEnd) {
      message.error("Start time must be before end time");
      return;
    }
    if (!labSlotConfig.labCancellationPolicy.trim()) {
      message.error("Enter the cancellation policy shown to patients");
      return;
    }
    try {
      setSavingLabSlot(true);
      await pointsSettingAPI.updateLabSlotSettings(labSlotConfig);
      message.success("Lab slot settings saved");
    } catch {
      message.error("Failed to save lab slot settings");
    } finally {
      setSavingLabSlot(false);
    }
  };

  const addHoliday = (date) => {
    if (!date) return;
    const iso = date.format("YYYY-MM-DD");
    if (!labSlotConfig.holidays.includes(iso)) {
      setLabSlotConfig(c => ({ ...c, holidays: [...c.holidays, iso] }));
    }
  };

  const removeHoliday = (iso) => {
    setLabSlotConfig(c => ({ ...c, holidays: c.holidays.filter(h => h !== iso) }));
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await pointsSettingAPI.getSettings();


      // Access the data correctly from the response
      const data = response.data;

      if (response?.success && data) {
        setSettingsId(data._id);
        setCurrentPoints(data.transplantCurrentPoints ?? 0);
        setTargetPoints(data.transplantTargetPoints ?? 200000);
        setPointsConfig({
          doctorAppointment: data.doctorAppointment || 50,
          labTest: data.labTest || 30,
          referralPointsForReferrer: data.referralPointsForReferrer ?? 100,
          pointsToCurrencyRate: data.pointsToCurrencyRate ?? 1,
          referralPointsForNewUser: data.referralPointsForNewUser ?? 50,
        });
        setMilestones(data.milestones || []);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to load settings";
      message.error(errorMessage);
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle points configuration update
  const handlePointsUpdate = async () => {
    try {
      setSubmitting(true);
      const response = await pointsSettingAPI.updatePointsConfig(pointsConfig);


      if (response.success) {
        message.success(
          response.message || "Points configuration updated successfully!"
        );

        // Update the local state with the new data
        const data = response.data;
        if (data) {
          setPointsConfig({
            doctorAppointment: data.doctorAppointment,
            labTest: data.labTest,
            referralPointsForReferrer: data.referralPointsForReferrer ?? 100,
          pointsToCurrencyRate: data.pointsToCurrencyRate ?? 1,
            referralPointsForNewUser: data.referralPointsForNewUser ?? 50,
          });
          setMilestones(data.milestones || []);
        }
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update points configuration";
      message.error(errorMessage);
      console.error("Error updating points:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle milestone modal
  const showMilestoneModal = (milestone = null) => {
    setEditingMilestone(milestone);
    if (milestone) {
      form.setFieldsValue({
        points: milestone.points,
        award: milestone.award,
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleMilestoneSubmit = async (values) => {
    try {
      setSubmitting(true);

      let response;

      if (editingMilestone) {
        // Update existing milestone
        response = await pointsSettingAPI.updateMilestone(
          editingMilestone._id,
          values
        );
      } else {
        // Add new milestone
        response = await pointsSettingAPI.addMilestone(values);
      }


      if (response.success) {
        message.success(
          response.message || "Milestone saved successfully!"
        );

        // Update the local state with the new data
        const data = response.data;
        if (data) {
          setMilestones(data.milestones || []);
        }
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingMilestone(null);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to save milestone";
      message.error(errorMessage);
      console.error("Error saving milestone:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    try {
      setSubmitting(true);
      const response = await pointsSettingAPI.deleteMilestone(milestoneId);


      if (response?.success) {
        message.success(response.message || "Milestone deleted successfully!");

        // Update the local state with the new data
        const data = response.data;
        if (data) {
          setMilestones(data.milestones || []);
        }
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete milestone";
      message.error(errorMessage);
      console.error("Error deleting milestone:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const updateClaim = async (id, status) => {
    try {
      await rewardsAPI.updateClaim(id, { status });
      message.success(`Request ${status}`);
      await fetchClaims();
    } catch { message.error('Unable to update claim'); }
  };
  const handleApproveRequest = id => updateClaim(id, 'approved');
  const handleRejectRequest = id => updateClaim(id, 'rejected');

  // Milestones table columns
  const milestoneColumns = [
    {
      title: "Points Required",
      dataIndex: "points",
      key: "points",
      render: (points) => (
        <Text strong style={{ fontSize: "16px", color: theme.colors.primary }}>
          {points} Points
        </Text>
      ),
    },
    {
      title: "Award",
      dataIndex: "award",
      key: "award",
      render: (award) => (
        <Space>
          <GiftOutlined style={{ color: theme.colors.secondary }} />
          <Text>{award}</Text>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showMilestoneModal(record)}
            style={{ color: theme.colors.primary }}
            disabled={submitting}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Milestone"
            description="Are you sure you want to delete this milestone?"
            onConfirm={() => handleDeleteMilestone(record._id)}
            okText="Yes"
            cancelText="No"
            disabled={submitting}
          >
            <Button danger icon={<DeleteOutlined />} disabled={submitting}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // User requests table columns
  const requestColumns = [
    {
      title: "User Details",
      key: "user",
      render: (_, record) => (
        <div>
          <Text strong>{record.userName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.email}
          </Text>
        </div>
      ),
    },
    {
      title: "Current Points",
      dataIndex: "currentPoints",
      key: "currentPoints",
      render: (points) => (
        <Tag
          color={theme.colors.primary}
          style={{ fontSize: "14px", padding: "4px 12px" }}
        >
          {points} Points
        </Tag>
      ),
    },
    {
      title: "Requested Award",
      key: "milestone",
      render: (_, record) => (
        <div>
          <Text strong>{record.requestedMilestone}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            ({record.requestedPoints} points)
          </Text>
        </div>
      ),
    },
    {
      title: "Request Date",
      dataIndex: "requestDate",
      key: "requestDate",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const config = {
          pending: { color: "orange", text: "Pending" },
          approved: { color: "green", text: "Approved" },
          rejected: { color: "red", text: "Rejected" },
        };
  return (
          <StatusTag color={config[status].color}>
            {config[status].text}
          </StatusTag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) =>
        record.status === "pending" ? (
          <Space>
            <Popconfirm
              title="Approve Request"
              description="Are you sure you want to approve this claim?"
              onConfirm={() => handleApproveRequest(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                icon={<CheckOutlined />}
                style={{ backgroundColor: theme.colors.success }}
              >
                Approve
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Reject Request"
              description="Are you sure you want to reject this claim?"
              onConfirm={() => handleRejectRequest(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button danger icon={<CloseOutlined />}>
                Reject
              </Button>
            </Popconfirm>
          </Space>
        ) : (
          <Text type="secondary">No actions available</Text>
        ),
    },
  ];

  if (claimsOnly) return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <PageHero title="Reward claim requests" subtitle="Review patient and doctor reward requests and their approval status." scene="rewards" />
        <StyledCard title="User Claim Requests">
          <Table columns={requestColumns} dataSource={userRequests} rowKey="id" loading={claimsLoading}
            pagination={{ current: claimPage, total: claimTotal, onChange: setClaimPage, pageSize: 10,
              showSizeChanger: false, showTotal: total => `Total ${total} requests` }} />
        </StyledCard>
      </PageContainer>
    </ThemeProvider>
  );

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <LoadingContainer>
          <Spin size="large" tip="Loading settings..." />
        </LoadingContainer>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <PageHero title="Rewards & fees" subtitle="How patients and doctors earn points, what a point is worth, and the fees you charge." scene="rewards" />

        {/* Points Configuration Section */}
        <StyledCard
          title={
            <>
              <SettingOutlined /> Points Configuration
            </>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <PointsInputCard>
                <InputLabel>Points per Doctor Appointment</InputLabel>
                <Text>5 points per successful paid booking</Text>
              </PointsInputCard>
            </Col>
            <Col xs={24} md={12}>
              <PointsInputCard>
                <InputLabel>Points per Lab Booking</InputLabel>
                <Text>5 points per booking, regardless of test count</Text>
              </PointsInputCard>
            </Col>
            <Col xs={24} md={12}>
              <PointsInputCard>
                <InputLabel>
                  Value of One Point (₹)
                </InputLabel>
                <StyledInputNumber
                  min={0}
                  max={1000}
                  value={pointsConfig.pointsToCurrencyRate}
                  onChange={(value) =>
                    setPointsConfig({ ...pointsConfig, pointsToCurrencyRate: value })
                  }
                  placeholder="Enter rupees"
                  disabled={submitting}
                />
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                  Rupees knocked off a bill per point redeemed. Only members can redeem.
                </div>
              </PointsInputCard>
            </Col>
            <Col xs={24} md={12}>
              <PointsInputCard>
                <InputLabel>
                  Points for Referrer
                </InputLabel>
                <StyledInputNumber
                  min={0}
                  max={1000}
                  value={pointsConfig.referralPointsForReferrer}
                  onChange={(value) =>
                    setPointsConfig({ ...pointsConfig, referralPointsForReferrer: value })
                  }
                  placeholder="Enter points"
                  disabled={submitting}
                />
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                  Points awarded to the person who shared their referral code
                </div>
              </PointsInputCard>
            </Col>
            <Col xs={24} md={12}>
              <PointsInputCard>
                <InputLabel>
                  Points for New User (Referred)
                </InputLabel>
                <StyledInputNumber
                  min={0}
                  max={1000}
                  value={pointsConfig.referralPointsForNewUser}
                  onChange={(value) =>
                    setPointsConfig({ ...pointsConfig, referralPointsForNewUser: value })
                  }
                  placeholder="Enter points"
                  disabled={submitting}
                />
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                  Welcome bonus points awarded to the new user who used a referral code
                </div>
              </PointsInputCard>
            </Col>
          </Row>
          <Divider />
          <GradientButton
            size="large"
            onClick={handlePointsUpdate}
            block
            loading={submitting}
          >
            Save Points Configuration
          </GradientButton>
        </StyledCard>

        {/* Profile Verification Fee */}
        <StyledCard
          title={
            <>
              <CheckOutlined /> Profile Verification Fee
            </>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <PointsInputCard>
                <InputLabel>Fee Amount (₹)</InputLabel>
                <StyledInputNumber
                  min={0}
                  value={profileVerificationFee}
                  onChange={(v) => setProfileVerificationFee(v ?? 0)}
                  placeholder="Enter fee in rupees"
                  disabled={savingVerificationFee}
                  addonBefore="₹"
                />
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                  Set to 0 to make profile verification free
                </div>
              </PointsInputCard>
            </Col>
          </Row>
          <Divider />
          <GradientButton
            size="large"
            onClick={handleSaveVerificationFee}
            loading={savingVerificationFee}
            block
          >
            Save Verification Fee
          </GradientButton>
        </StyledCard>

        {/* Lab Time Slot Settings */}
        <StyledCard
          title={
            <>
              <ClockCircleOutlined /> Lab Test Time Slot Settings
            </>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <PointsInputCard>
                <InputLabel>Slot Start Hour (0–23)</InputLabel>
                <StyledInputNumber
                  min={0}
                  max={23}
                  value={labSlotConfig.labSlotStart}
                  onChange={v => setLabSlotConfig(c => ({ ...c, labSlotStart: v }))}
                  addonAfter="hr"
                />
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                  Default: 8 (8:00 AM)
                </div>
              </PointsInputCard>
            </Col>
            <Col xs={24} sm={12}>
              <PointsInputCard>
                <InputLabel>Slot End Hour (1–24)</InputLabel>
                <StyledInputNumber
                  min={1}
                  max={24}
                  value={labSlotConfig.labSlotEnd}
                  onChange={v => setLabSlotConfig(c => ({ ...c, labSlotEnd: v }))}
                  addonAfter="hr"
                />
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                  Default: 18 (6:00 PM)
                </div>
              </PointsInputCard>
            </Col>
          </Row>

          <Divider orientation="left" style={{ marginTop: 20 }}>
            <CalendarOutlined /> Holidays (slots disabled on these dates)
          </Divider>
          <Space wrap style={{ marginBottom: 12 }}>
            {labSlotConfig.holidays.map(iso => (
              <Tag
                key={iso}
                closable
                onClose={() => removeHoliday(iso)}
                color="red"
              >
                {iso}
              </Tag>
            ))}
            {labSlotConfig.holidays.length === 0 && (
              <span style={{ color: '#6B7280', fontSize: 13 }}>No holidays marked</span>
            )}
          </Space>
          <div style={{ marginBottom: 16 }}>
            <DatePicker
              placeholder="Add holiday date"
              onChange={addHoliday}
              value={null}
              disabledDate={d => labSlotConfig.holidays.includes(d.format("YYYY-MM-DD"))}
              style={{ marginRight: 8 }}
            />
          </div>
          <Divider orientation="left" style={{ marginTop: 20 }}>
            Cancellation policy
          </Divider>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <PointsInputCard>
                <InputLabel>Patients can cancel until</InputLabel>
                <StyledInputNumber
                  min={0}
                  max={168}
                  precision={0}
                  value={labSlotConfig.labCancellationHours}
                  onChange={v => setLabSlotConfig(c => ({ ...c, labCancellationHours: v }))}
                  addonAfter="hr before"
                />
              </PointsInputCard>
            </Col>
            <Col xs={24} sm={16}>
              <PointsInputCard>
                <InputLabel>Policy text shown to patients</InputLabel>
                <Input.TextArea
                  rows={3}
                  maxLength={1000}
                  value={labSlotConfig.labCancellationPolicy}
                  onChange={e => setLabSlotConfig(c => ({ ...c, labCancellationPolicy: e.target.value }))}
                />
              </PointsInputCard>
            </Col>
          </Row>
          <GradientButton
            size="large"
            onClick={handleSaveLabSlotSettings}
            loading={savingLabSlot}
            block
          >
            Save Lab Slot Settings
          </GradientButton>
        </StyledCard>

        <StyledCard title="Public transplant progress">
          <p>Earned points are automatic: 50 per paid membership and 5 per paid appointment or test booking. Update the target required for kidney transplant support.</p>
          <Form layout="vertical" onFinish={async () => {
            setSavingProgress(true);
            try { const response = await pointsSettingAPI.updateTransplantProgress(targetPoints); setTargetPoints(response.data.transplantTargetPoints); message.success('Transplant target saved'); }
            catch (error) { message.error(error.response?.data?.message || 'Unable to save progress'); }
            finally { setSavingProgress(false); }
          }}>
            <p>Current points: {currentPoints.toLocaleString('en-IN')} (automatic)</p>
            <Form.Item label="Target points"><InputNumber aria-label="Transplant target points" min={1} max={Number.MAX_SAFE_INTEGER} precision={0} value={targetPoints} onChange={setTargetPoints} /></Form.Item>
            <Button type="primary" htmlType="submit" loading={savingProgress} disabled={!Number.isSafeInteger(targetPoints) || targetPoints < 1}>Save transplant target</Button>
          </Form>
        </StyledCard>
        {/* Milestones Management Section */}
        <StyledCard
          title={
            <>
              <GiftOutlined /> Rewards Milestones
            </>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showMilestoneModal()}
              style={{ backgroundColor: theme.colors.secondary }}
              disabled={submitting}
            >
              Add Milestone
            </Button>
          }
        >
          <Table
            columns={milestoneColumns}
            dataSource={milestones}
            rowKey="_id"
            pagination={false}
            loading={submitting}
            locale={{
              emptyText:
                'No milestones added yet. Click "Add Milestone" to create one.',
            }}
          />
        </StyledCard>

        {/* Add/Edit Milestone Modal */}
        <Modal
          title={editingMilestone ? "Edit Milestone" : "Add New Milestone"}
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
            setEditingMilestone(null);
          }}
          footer={null}
        >
          <Form form={form} layout="vertical" onFinish={handleMilestoneSubmit}>
            <Form.Item
              label="Points Required"
              name="points"
              rules={[
                { required: true, message: "Please enter points!" },
                {
                  type: "number",
                  min: 1,
                  message: "Points must be at least 1",
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={1}
                max={Number.MAX_SAFE_INTEGER}
                placeholder="Enter points required"
                disabled={submitting}
              />
            </Form.Item>

            <Form.Item
              label="Award Name"
              name="award"
              rules={[{ required: true, message: "Please enter award name!" }]}
            >
              <Input
                placeholder="Enter award name (e.g., Medical Check-up)"
                disabled={submitting}
              />
            </Form.Item>

            <Form.Item>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                    setEditingMilestone(null);
                  }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <GradientButton htmlType="submit" loading={submitting}>
                  {editingMilestone ? "Update" : "Add"} Milestone
                </GradientButton>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </PageContainer>
    </ThemeProvider>
  );
};

export default PointsSettingsPage;
